/**
 * GeneratingView - Shown while trip is being generated
 * Layout: Map with destination pin | Chat with summary + pace question
 * Flow: Summary → Pace question → Loading
 */
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getTripStatus } from '../../services/api';
import { logger } from '../../utils/logger';
import './GeneratingView.css';

// Orange marker icon
const orangeIcon = new L.Icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(`
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="48" viewBox="0 0 32 48">
            <path fill="#f97316" stroke="#ea580c" stroke-width="1.5" d="M16 0C7.2 0 0 7.2 0 16c0 16 16 32 16 32s16-16 16-32C32 7.2 24.8 0 16 0z"/>
            <circle fill="#fff" cx="16" cy="16" r="6"/>
        </svg>
    `),
    iconSize: [32, 48],
    iconAnchor: [16, 48],
    popupAnchor: [0, -48],
});

interface GeneratingViewProps {
    tripId: string;
    slug: string;
    patchQuestion: string | null;
    hasAnswered: boolean;
    showLoadingModal: boolean;
    onAnswer: (answer: string) => void;
    onReady: () => void;
}

interface TripSummary {
    destinations: string[];
    travelers: number;
    dateRange: { flexible: true } | { start: string; end: string };
}

// Get destination info from sessionStorage
function getDestinationFromStorage(tripId: string): { name: string; lat: number; lng: number } | null {
    try {
        const stored = sessionStorage.getItem(`trip_${tripId}_destination`);
        if (stored) {
            return JSON.parse(stored);
        }
    } catch (e) {
        logger.error('Failed to parse destination from storage:', e);
    }
    return null;
}

// Get trip summary from sessionStorage
function getTripSummaryFromStorage(tripId: string): TripSummary | null {
    try {
        const stored = sessionStorage.getItem(`trip_${tripId}_summary`);
        if (stored) {
            return JSON.parse(stored);
        }
    } catch (e) {
        logger.error('Failed to parse trip summary from storage:', e);
    }
    return null;
}

// Calculate days from date range
function calculateDays(dateRange: TripSummary['dateRange']): string | null {
    if ('flexible' in dateRange) return null;
    try {
        const start = new Date(dateRange.start);
        const end = new Date(dateRange.end);
        const diffTime = Math.abs(end.getTime() - start.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        return String(diffDays);
    } catch {
        return null;
    }
}

type PaceOption = 'relaxed' | 'balanced' | 'intense';

// SVG Icons for summary
const PlaneIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/>
    </svg>
);

const CalendarIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect width="18" height="18" x="3" y="4" rx="2" ry="2"/>
        <line x1="16" x2="16" y1="2" y2="6"/>
        <line x1="8" x2="8" y1="2" y2="6"/>
        <line x1="3" x2="21" y1="10" y2="10"/>
    </svg>
);

const UsersIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
);

const CheckIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"/>
    </svg>
);

export default function GeneratingView({
    tripId,
    slug,
    hasAnswered,
    showLoadingModal,
    onAnswer,
    onReady,
}: GeneratingViewProps) {
    const { t, i18n } = useTranslation();
    const [selectedPace, setSelectedPace] = useState<PaceOption | null>(null);
    const [showFeedback, setShowFeedback] = useState(false);
    const [messageIndex, setMessageIndex] = useState(0);
    const [showMap, setShowMap] = useState(() => typeof window !== 'undefined' && window.innerWidth > 900);
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<L.Map | null>(null);

    const destination = getDestinationFromStorage(tripId);
    const tripSummary = getTripSummaryFromStorage(tripId);
    const days = tripSummary?.dateRange ? calculateDays(tripSummary.dateRange) : null;
    const destinationName = destination?.name || tripSummary?.destinations?.[0] || 'Destination';

    // Contextual loading messages based on destination
    const getLoadingMessages = () => [
        t('generating.msg_selecting', { 
            destination: destinationName,
            defaultValue: `Selecting must-see places in ${destinationName}...`
        }),
        t('generating.msg_balancing', { 
            defaultValue: 'Balancing sightseeing and free time...'
        }),
        t('generating.msg_optimizing', { 
            defaultValue: 'Optimizing daily routes...'
        }),
        t('generating.msg_adding_local', { 
            destination: destinationName,
            defaultValue: `Adding local recommendations from ${destinationName}...`
        }),
        t('generating.msg_almost', { 
            defaultValue: 'Almost there...'
        }),
    ];

    const loadingMessages = getLoadingMessages();

    // Pace options
    const paceOptions: { key: PaceOption; label: string; desc: string }[] = [
        { 
            key: 'relaxed', 
            label: t('generating.pace_relaxed', 'Relaxed'),
            desc: t('generating.pace_relaxed_desc', 'More free time, fewer activities')
        },
        { 
            key: 'balanced', 
            label: t('generating.pace_balanced', 'Balanced'),
            desc: t('generating.pace_balanced_desc', 'Mix of activities and rest')
        },
        { 
            key: 'intense', 
            label: t('generating.pace_intense', 'Fast-paced'),
            desc: t('generating.pace_intense_desc', 'See as much as possible')
        },
    ];

    // Initialize map with destination — only when showMap (desktop); never mount on mobile
    useEffect(() => {
        if (!showMap || !mapContainerRef.current || mapRef.current) return;

        const destCoords = destination || { lat: 48.8566, lng: 2.3522 };

        const map = L.map(mapContainerRef.current).setView([destCoords.lat, destCoords.lng], 12);
        
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        }).addTo(map);

        // Add destination marker
        const marker = L.marker([destCoords.lat, destCoords.lng], { icon: orangeIcon }).addTo(map);
        
        // Create popup content safely
        const popupDiv = document.createElement('div');
        popupDiv.style.textAlign = 'center';
        popupDiv.style.padding = '4px';
        
        const title = document.createElement('strong');
        title.textContent = destinationName;
        title.style.fontSize = '14px';
        title.style.color = '#1e293b';
        popupDiv.appendChild(title);
        
        const subtitle = document.createElement('div');
        subtitle.textContent = t('generating.your_destination', 'Your destination');
        subtitle.style.fontSize = '12px';
        subtitle.style.color = '#64748b';
        subtitle.style.marginTop = '4px';
        popupDiv.appendChild(subtitle);
        
        marker.bindPopup(popupDiv).openPopup();

        mapRef.current = map;

        return () => {
            map.remove();
            mapRef.current = null;
        };
    }, [showMap, tripId, t, destinationName, destination]);

    // Mobile/tablet: hide header and do NOT mount map (avoids Leaflet breaking layout on iOS)
    useEffect(() => {
        const updateLayout = () => {
            const narrow = window.innerWidth <= 900;
            setShowMap(!narrow);
            if (narrow) {
                document.body.classList.add('generating-view-mobile');
            } else {
                document.body.classList.remove('generating-view-mobile');
            }
        };
        updateLayout();
        window.addEventListener('resize', updateLayout);
        return () => {
            window.removeEventListener('resize', updateLayout);
            document.body.classList.remove('generating-view-mobile');
        };
    }, []);

    // Rotate loading messages
    useEffect(() => {
        if (!showLoadingModal) return;
        const interval = setInterval(() => {
            setMessageIndex((prev) => (prev + 1) % loadingMessages.length);
        }, 3000);
        return () => clearInterval(interval);
    }, [showLoadingModal, loadingMessages.length]);

    // Poll for completion when loading modal is shown
    useEffect(() => {
        if (!showLoadingModal) return;

        const pollInterval = setInterval(async () => {
            try {
                const status = await getTripStatus(slug);
                if (status.ready) {
                    clearInterval(pollInterval);
                    onReady();
                }
            } catch (err) {
                logger.error('Poll failed:', err);
            }
        }, 2000);

        return () => clearInterval(pollInterval);
    }, [showLoadingModal, slug, onReady]);

    const handlePaceSelect = (pace: PaceOption) => {
        setSelectedPace(pace);
        setShowFeedback(true);
        
        // Show feedback for a moment, then submit
        setTimeout(() => {
            // Map pace to answer string for backend
            const paceAnswers: Record<PaceOption, string> = {
                relaxed: 'relaxed pace with more free time',
                balanced: 'balanced pace with mix of activities and rest',
                intense: 'fast-paced to see as much as possible',
            };
            onAnswer(paceAnswers[pace]);
        }, 800);
    };

    // Get feedback message based on selected pace
    const getFeedbackMessage = () => {
        if (!selectedPace) return '';
        const messages: Record<PaceOption, string> = {
            relaxed: t('generating.feedback_relaxed', "Got it — I'll keep things relaxed."),
            balanced: t('generating.feedback_balanced', "Got it — I'll balance activities and rest."),
            intense: t('generating.feedback_intense', "Got it — I'll pack in as much as possible."),
        };
        return messages[selectedPace];
    };

    // Format travelers text
    const getTravelersText = () => {
        if (!tripSummary) return '';
        const count = tripSummary.travelers;
        if (i18n.language === 'ro') {
            return count === 1 ? '1 călător' : `${count} călători`;
        }
        return count === 1 ? '1 traveler' : `${count} travelers`;
    };

    // Format days text
    const getDaysText = () => {
        if (!days) return t('generating.flexible_dates', 'flexible dates');
        if (i18n.language === 'ro') {
            return days === '1' ? '1 zi' : `${days} zile`;
        }
        return days === '1' ? '1 day' : `${days} days`;
    };

    return (
        <div className="generating-view">
            {/* Map only on desktop (>900px); never mount on mobile so Leaflet doesn't break layout */}
            {showMap && (
                <div className="generating-map">
                    <div className="map-container" ref={mapContainerRef} />
                </div>
            )}

            {/* Chat panel */}
            <div className="generating-chat">
                <div className="chat-header">
                    <div className="raisa-avatar">
                        <img src="/raisa.png" alt="Raisa" />
                    </div>
                    <div className="chat-header-info">
                        <h3>Raisa</h3>
                        <span className="status-badge">
                            <span className="status-dot" />
                            {t('generating.creating_trip', 'Creating your trip...')}
                        </span>
                    </div>
                </div>

                <div className="chat-messages">
                    {/* AI Summary Message - First thing user sees */}
                    <div className="message ai-message">
                        <div className="message-avatar">
                            <img src="/raisa.png" alt="Raisa" />
                        </div>
                        <div className="message-bubble summary-bubble">
                            <p className="summary-intro">
                                {t('generating.summary_intro', "Great! Here's what I'm planning for you:")}
                            </p>
                            <div className="trip-summary">
                                <div className="summary-item">
                                    <span className="summary-icon"><PlaneIcon /></span>
                                    <span>{destinationName}</span>
                                </div>
                                <div className="summary-item">
                                    <span className="summary-icon"><CalendarIcon /></span>
                                    <span>{getDaysText()}</span>
                                </div>
                                <div className="summary-item">
                                    <span className="summary-icon"><UsersIcon /></span>
                                    <span>{getTravelersText()}</span>
                                </div>
                            </div>
                            <p className="summary-working">
                                {t('generating.summary_working', "I'm putting together your itinerary now.")}
                            </p>
                        </div>
                    </div>

                    {/* AI Pace Question - Second message */}
                    {!hasAnswered && !showFeedback && (
                        <div className="message ai-message">
                            <div className="message-avatar">
                                <img src="/raisa.png" alt="Raisa" />
                            </div>
                            <div className="message-bubble question-bubble">
                                <p className="pace-context">
                                    {t('generating.pace_context', 'This helps me balance your days.')}
                                </p>
                                <p className="pace-question">
                                    {t('generating.pace_question', 'How would you like the pace of this trip to be?')}
                                </p>
                                <div className="pace-options">
                                    {paceOptions.map((option) => (
                                        <button
                                            key={option.key}
                                            className={`pace-option ${selectedPace === option.key ? 'selected' : ''}`}
                                            onClick={() => handlePaceSelect(option.key)}
                                            disabled={selectedPace !== null}
                                        >
                                            <span className="pace-label">{option.label}</span>
                                            <span className="pace-desc">{option.desc}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Instant Feedback after selection */}
                    {showFeedback && !hasAnswered && selectedPace && (
                        <>
                            <div className="message user-message">
                                <div className="message-bubble">
                                    <p>{paceOptions.find(p => p.key === selectedPace)?.label}</p>
                                </div>
                            </div>
                            <div className="message ai-message feedback-message">
                                <div className="message-avatar">
                                    <img src="/raisa.png" alt="Raisa" />
                                </div>
                                <div className="message-bubble feedback-bubble">
                                    <span className="feedback-check"><CheckIcon /></span>
                                    <p>{getFeedbackMessage()}</p>
                                </div>
                            </div>
                        </>
                    )}

                    {/* User Answer (after submitted to backend) */}
                    {hasAnswered && selectedPace && (
                        <div className="message user-message">
                            <div className="message-bubble">
                                <p>{paceOptions.find(p => p.key === selectedPace)?.label}</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Waiting indicator after answer */}
                {hasAnswered && (
                    <div className="chat-waiting">
                        <div className="typing-indicator">
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                        <p>{t('generating.raisa_personalizing', 'Raisa is personalizing your trip...')}</p>
                    </div>
                )}
            </div>

            {/* Loading Modal */}
            {showLoadingModal && (
                <div className="loading-modal-overlay">
                    <div className="loading-modal">
                        <div className="loading-animation">
                            <div className="globe">
                                <svg viewBox="0 0 100 100" width="80" height="80">
                                    <circle cx="50" cy="50" r="45" fill="none" stroke="#f97316" strokeWidth="2" opacity="0.3" />
                                    <circle cx="50" cy="50" r="45" fill="none" stroke="#f97316" strokeWidth="3" strokeDasharray="70 200" className="globe-spinner" />
                                    <path d="M50 5 Q65 25 50 50 Q35 75 50 95" fill="none" stroke="#f97316" strokeWidth="1.5" opacity="0.5" />
                                    <path d="M5 50 Q25 35 50 50 Q75 65 95 50" fill="none" stroke="#f97316" strokeWidth="1.5" opacity="0.5" />
                                </svg>
                            </div>
                        </div>
                        <h2>{t('generating.modal_title', 'Raisa is creating your trip')}</h2>
                        <p className="loading-message">{loadingMessages[messageIndex]}</p>
                        <div className="progress-bar">
                            <div className="progress-fill" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
